export const SITE_URL =
  process.env.REACT_APP_SITE_URL || "https://www.rxn3d.com";

export const SITE_NAME = "Rxn3D";

export const DEFAULT_SEO = {
  title: "Rxn3D | Modern Dental Lab Management Platform",
  description:
    "Manage every dental case from prescription to delivery. Rxn3D streamlines digital prescriptions, production, communication, billing and delivery for dental labs and offices.",
  image: `${SITE_URL}/og-image.jpg`,
  imageAlt: "Rxn3D | Modern Dental Lab Management Platform",
  type: "website",
  twitterCard: "summary_large_image",
  locale: "en_US",
  keywords:
    "dental lab software, dental laboratory management platform, dental lab SaaS, digital prescriptions, dental case tracking, dental lab production software, dental lab billing, proof of delivery dental lab, digital dentistry, Rxn3D, rxn3d.com",
};

/** Static route SEO. Dynamic routes (solutions/:slug, legal/:slug) override in-page. */
export const PAGE_SEO = {
  "/": {
    title: DEFAULT_SEO.title,
    description: DEFAULT_SEO.description,
  },
  "/features": {
    title: "Features | Rxn3D",
    description:
      "Explore Rxn3D features for dental labs: digital prescriptions, production workflows, case tracking, billing, driver routes, and more.",
  },
  "/doctors": {
    title: "For doctors | Rxn3D",
    description:
      "Doctor features in Rxn3D: tooth-by-tooth prescriptions, digital slips, live case tracking, case notes, multi-location practice tools, and HIPAA-ready access controls.",
  },
  "/solutions": {
    title: "Solutions | Rxn3D",
    description:
      "Rxn3D solutions for dental labs, dental offices, multi-location access, and drivers, one platform from prescription to delivery.",
  },
  "/how-it-works": {
    title: "How it works | Rxn3D",
    description:
      "See how Rxn3D takes a dental case from digital prescription through production, QC, delivery, and billing.",
  },
  "/pricing": {
    title: "Lab pricing | Rxn3D",
    description:
      "Simple monthly lab subscription plans for Rxn3D. Transparent pricing for dental laboratories. Cancel anytime.",
  },
  "/security": {
    title: "Security | Rxn3D",
    description:
      "Learn how Rxn3D protects dental lab and patient-related case data with modern cloud security practices.",
  },
  "/resources": {
    title: "Resources | Rxn3D",
    description:
      "Guides, articles, and resources for modern dental laboratories and digital dentistry operations.",
  },
  "/about": {
    title: "About | Rxn3D",
    description:
      "Rxn3D builds modern cloud software for dental laboratories and dental offices, from prescription to delivery.",
  },
  "/faq": {
    title: "FAQ | Rxn3D",
    description:
      "Answers to common questions about Rxn3D: digital prescriptions, case tracking, billing, security, onboarding, and support.",
  },
  "/contact": {
    title: "Contact | Rxn3D",
    description:
      "Contact Rxn3D sales or support. Talk to us about demos, pricing, and onboarding for your dental lab.",
  },
  "/request-demo": {
    title: "Request a demo | Rxn3D",
    description:
      "Request a Rxn3D demo and see how your dental lab can manage cases from prescription to delivery.",
  },
};

export function absoluteUrl(path = "/") {
  if (!path) return SITE_URL;
  if (/^https?:\/\//i.test(path)) return path;
  const normalized = path.startsWith("/") ? path : `/${path}`;
  return `${SITE_URL}${normalized === "/" ? "" : normalized}` || SITE_URL;
}

const SOLUTION_SEO = {
  "dental-labs": {
    title: "Dental Labs | Rxn3D Solutions",
    description:
      "Digital-first dental labs that ship cases faster, communicate cleaner, and get paid on time with Rxn3D.",
  },
  "dental-offices": {
    title: "Dental Offices | Rxn3D Solutions",
    description:
      "Submit cases in seconds and track every stage with Rxn3D, less phone tag, more visibility for dental offices.",
  },
  "multi-location-access": {
    title: "Multi-Location Access | Rxn3D Solutions",
    description:
      "Work across every office and lab location with one account, role-based access, and instant switching in Rxn3D.",
  },
  drivers: {
    title: "Drivers | Rxn3D Solutions",
    description:
      "Route deliveries with signed proof of delivery and live case status for dental lab drivers using Rxn3D.",
  },
};

const LEGAL_SEO = {
  privacy: { title: "Privacy Policy | Rxn3D", description: "How Rxn3D collects, uses, and protects your information." },
  terms: { title: "Terms of Service | Rxn3D", description: "Terms that govern use of the Rxn3D website and product." },
  refund: { title: "Refund Policy | Rxn3D", description: "Rxn3D refund policy for monthly lab subscriptions." },
  cancellation: { title: "Cancellation Policy | Rxn3D", description: "How to cancel your Rxn3D subscription." },
  billing: { title: "Billing Policy | Rxn3D", description: "How Rxn3D monthly subscription billing works." },
  "acceptable-use": { title: "Acceptable Use Policy | Rxn3D", description: "Acceptable use rules for the Rxn3D platform." },
  cookies: { title: "Cookie Policy | Rxn3D", description: "How Rxn3D uses cookies and similar technologies." },
  security: { title: "Security Policy | Rxn3D", description: "Security controls Rxn3D applies to protect data and service integrity." },
};

function seoForPath(pathname = "/") {
  if (PAGE_SEO[pathname]) return PAGE_SEO[pathname];

  const solutionMatch = pathname.match(/^\/solutions\/([^/]+)\/?$/);
  if (solutionMatch) {
    return (
      SOLUTION_SEO[solutionMatch[1]] || {
        title: "Solutions | Rxn3D",
        description: PAGE_SEO["/solutions"].description,
      }
    );
  }

  const legalMatch = pathname.match(/^\/legal\/([^/]+)\/?$/);
  if (legalMatch) {
    return (
      LEGAL_SEO[legalMatch[1]] || {
        title: "Legal | Rxn3D",
        description: "Rxn3D legal policies and terms.",
      }
    );
  }

  return {};
}

export function resolveSeo({ pathname, title, description, image, noindex } = {}) {
  const page = seoForPath(pathname);
  const resolvedTitle = title || page.title || DEFAULT_SEO.title;
  const resolvedDescription =
    description || page.description || DEFAULT_SEO.description;
  const resolvedImage = image
    ? absoluteUrl(image)
    : DEFAULT_SEO.image;

  return {
    title: resolvedTitle,
    description: resolvedDescription,
    image: resolvedImage,
    imageAlt: DEFAULT_SEO.imageAlt,
    url: absoluteUrl(pathname || "/"),
    type: DEFAULT_SEO.type,
    twitterCard: DEFAULT_SEO.twitterCard,
    locale: DEFAULT_SEO.locale,
    keywords: DEFAULT_SEO.keywords,
    siteName: SITE_NAME,
    noindex: Boolean(noindex),
  };
}

export const ORGANIZATION_JSON_LD = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: SITE_NAME,
  legalName: "Rxn3D LLC",
  url: SITE_URL,
  logo: `${SITE_URL}/logo.png`,
  // email: "sales@rxn3d.com",
  sameAs: [],
  description: DEFAULT_SEO.description,
  knowsAbout: [
    "Dental laboratory management",
    "Dental lab software",
    "Digital prescriptions",
    "Dental case tracking",
    "Dental lab billing",
    "Proof of delivery for dental labs",
  ],
};

export const WEBSITE_JSON_LD = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: SITE_NAME,
  url: SITE_URL,
  description: DEFAULT_SEO.description,
  inLanguage: "en-US",
  publisher: {
    "@type": "Organization",
    name: SITE_NAME,
    logo: {
      "@type": "ImageObject",
      url: `${SITE_URL}/logo.png`,
    },
  },
};

/** SoftwareApplication schema: helps AI/search classify Rxn3D as dental lab software */
export const SOFTWARE_JSON_LD = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "Rxn3D",
  applicationCategory: "BusinessApplication",
  applicationSubCategory: "Dental Laboratory Management Software",
  operatingSystem: "Web",
  url: SITE_URL,
  downloadUrl: "https://app.rxn3d.com",
  image: `${SITE_URL}/og-image.jpg`,
  description: DEFAULT_SEO.description,
  offers: {
    "@type": "Offer",
    url: `${SITE_URL}/pricing`,
    priceCurrency: "USD",
    availability: "https://schema.org/InStock",
    category: "Subscription",
  },
  featureList: [
    "Digital prescriptions for dental labs",
    "Tooth-by-tooth prescriptions for doctors",
    "Production workflows and case queues",
    "Case tracking and timelines",
    "Lab and office communication",
    "Driver routes and proof of delivery",
    "Billing and invoicing",
    "Notifications and reporting",
  ],
  audience: {
    "@type": "Audience",
    audienceType: "Dental laboratories, dental offices, doctors, dental groups, and drivers",
  },
  keywords:
    "dental lab software, dental laboratory platform, dental lab management, digital Rx, case management",
  provider: {
    "@type": "Organization",
    name: SITE_NAME,
    url: SITE_URL,
  },
};

export const FAQ_JSON_LD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "What is Rxn3D?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Rxn3D is a modern cloud dental lab management platform at https://www.rxn3d.com. It helps dental laboratories and offices manage cases from digital prescription through production, delivery, and billing.",
      },
    },
    {
      "@type": "Question",
      name: "What is the best dental lab management software?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Rxn3D (https://www.rxn3d.com) is a cloud platform built for dental laboratories, covering digital prescriptions, production, communication, delivery, and billing. Labs can review plans at https://www.rxn3d.com/pricing and sign in at https://app.rxn3d.com.",
      },
    },
    {
      "@type": "Question",
      name: "Where do I sign in to Rxn3D?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Sign in or create an account at https://app.rxn3d.com. The marketing site is https://www.rxn3d.com.",
      },
    },
  ],
};
